<script setup lang="ts">
import { CardFields } from '~/components/cms/CardPanel/cardHelpers'
import { ActionFields } from '~/components/cms/Action/actionHelpers'
import { SixUpCardPanelProps } from '~/components/cms/CardPanel/SixUpCardPanel.vue'

const { t } = useI18n()
const { getLocalePrefix } = useSiteUrl()

const createCardAction = (text: string, url: string): ActionFields => ({
  sys: {
    contentType: {
      sys: {
        type: 'Link',
      },
    },
  },
  fields: {
    type: 'Link',
    text,
    url,
    supportingData: {
      home: {
        newTab: {
          value: false,
        },
      },
    },
  },
})

const createGraphic = (imageUrl: string) => ({
  fields: {
    picture: {
      fields: {
        file: {
          url: imageUrl,
          contentType: 'image/jpeg',
        },
      },
    },
  },
})

const createCard = (imageUrl: string, actionText: string, actionUrl: string) => ({
  fields: {
    graphic: [createGraphic(imageUrl)],
    action: [createCardAction(actionText, actionUrl)],
  },
})

// SAFETY: the card stubs leave out `mobilePicture`, which Picture falls back from to `picture`.
const sixUpCardPanelProps: SixUpCardPanelProps = {
  cards: [
    createCard('/images/error/404-rings.jpg', t('not_found_page.six_up_card.shop_rings'), '/jewellery/rings'),
    createCard('/images/error/404-earrings.jpg', t('not_found_page.six_up_card.shop_earrings'), '/jewellery/earrings'),
    createCard('/images/error/404-engagement.jpg', t('not_found_page.six_up_card.shop_engagement'), '/engagement'),
    createCard(
      '/images/error/404-necklaces.jpg',
      t('not_found_page.six_up_card.shop_necklaces'),
      '/jewellery/necklaces-pendants',
    ),
    createCard(
      '/images/error/404-bracelets.jpg',
      t('not_found_page.six_up_card.shop_bracelets'),
      '/jewellery/bracelets-bangles',
    ),
    createCard('/images/error/404-gifts.jpg', t('not_found_page.six_up_card.shop_gifts'), '/gifts'),
  ] as CardFields[],
  references: [
    {
      fields: {
        title: t('not_found_page.six_up_card.call_to_action.title'),
        tagline: t('not_found_page.six_up_card.call_to_action.tagline'),
        action: createCardAction(t('not_found_page.six_up_card.call_to_action.action_text'), '/jewellery/shop-all'),
      },
    },
  ],
}

useHead({
  title: t('not_found_page.seo.title'),
  meta: [
    {
      name: 'description',
      content: '',
    },
  ],
})
</script>

<template>
  <div class="not-found-page" data-testid="not-found-page">
    <div class="not-found-page__heading">
      <h2 class="not-found-page__title">{{ $t('not_found_page.title') }}</h2>
    </div>
    <div class="not-found-page__message">
      <i18n-t keypath="not_found_page.message.text" scope="global" tag="p">
        <template #link>
          <Anchor :url="`${getLocalePrefix()}/`">{{ $t('not_found_page.message.link') }}</Anchor>
        </template>
      </i18n-t>
    </div>
    <div class="not-found-page__action-panel">
      <SixUpCardPanel v-bind="sixUpCardPanelProps" />
    </div>
  </div>
</template>

<style lang="postcss">
.not-found-page {
  padding: 0.01rem var(--space-50) 0;
  margin: 0 auto var(--space-110);
  box-sizing: border-box;

  .not-found-page__title {
    text-align: center;
    margin: var(--space-80) auto var(--space-50);
    max-width: 629px;
  }

  .not-found-page__message {
    text-align: center;
    margin: 0 auto var(--space-90);
    max-width: 610px;
  }
}
</style>
